import { DEVICE_GROUPS } from './constants';

/** Unique key for a device row — deviceId alone can repeat across families. */
export const deviceKey = (device) => `${device.groupKey}:${device.deviceId}`;

/**
 * Flattens every device of a house (all families, all rooms) into a single list
 * that carries the room it currently sits in.
 */
export const collectHouseDevices = (house) => {
  const rooms = Array.isArray(house?.rooms) ? house.rooms : [];

  return rooms.flatMap((room) =>
    DEVICE_GROUPS.flatMap((group) =>
      (room?.[group.key] || []).map((device) => {
        const name = typeof device?.name === 'string' ? device.name.trim() : '';
        return {
          deviceId: device?.deviceId,
          name: name || device?.deviceId,
          hasName: name !== '',
          type: group.type,
          groupKey: group.key,
          roomName: room?.roomName || 'Unassigned',
          roomId: room?._id,
          applianceCount: Array.isArray(device?.appliances) ? device.appliances.length : 0,
        };
      })
    )
  ).filter((device) => device.deviceId);
};

/** Groups a device list by room name, keeping the order rooms were listed in. */
export const groupDevicesByRoom = (devices) => {
  const rooms = [];
  const index = {};

  for (const device of devices) {
    if (index[device.roomName] === undefined) {
      index[device.roomName] = rooms.length;
      rooms.push({ roomName: device.roomName, devices: [] });
    }
    rooms[index[device.roomName]].devices.push(device);
  }

  return rooms;
};

export const matchesSearch = (device, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    device.name?.toLowerCase().includes(q) ||
    device.deviceId?.toLowerCase().includes(q) ||
    device.roomName?.toLowerCase().includes(q)
  );
};

/** react-select options for the property dropdowns, de-duplicated by house id. */
export const buildHouseOptions = (housesList) => {
  const seen = new Set();
  const options = [];

  for (const house of Array.isArray(housesList) ? housesList : []) {
    const value = house?.houseAccessCode;
    if (!value || seen.has(value)) continue;
    seen.add(value);
    options.push({ value, label: house?.houseName || value });
  }

  return options;
};

export const houseSummary = (house) => {
  if (!house) return null;
  const rooms = Array.isArray(house.rooms) ? house.rooms : [];
  return { rooms: rooms.length, devices: collectHouseDevices(house).length };
};
